import { useQuery, useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { ipdService, AdmissionListFilters, BedListFilters, AdmissionServiceInput, UpdateAdmissionDto } from '../ipdService';
import { invalidateRiskFundImpact } from './useRiskFund';

/**
 * Every cache slice that can change when an admission is created, moved,
 * discharged, cancelled, or deleted. Keeping this in one helper prevents the
 * list, board, beds, dashboard, and billing panels from drifting apart.
 */
function invalidateAdmissionQueries(queryClient: QueryClient) {
  const keys = [
    'admissions',
    'admission-charges',
    'admission-billing-status',
    'admission-staff',
    'unbilled-charges',
    'beds',
    'top-sheet',
    'dashboard',
  ];
  for (const key of keys) {
    queryClient.invalidateQueries({ queryKey: [key] });
  }
}

export function useBeds(status?: string) {
  return useQuery({
    queryKey: ['beds', status],
    queryFn: () => ipdService.getBeds(status),
    staleTime: 30000,
  });
}

/** Paginated + API-filtered beds list (used by the Beds list view). */
export function useBedsPaginated(page = 1, limit = 20, filters?: BedListFilters) {
  return useQuery({
    queryKey: ['beds', 'paginated', page, limit, filters],
    queryFn: () => ipdService.getBedsPaginated(page, limit, filters),
    staleTime: 15000,
    placeholderData: (previous) => previous,
  });
}

export function useAdmissions(status?: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['admissions', status],
    queryFn: () => ipdService.getAdmissions(status),
    staleTime: 30000,
    enabled: options?.enabled ?? true,
  });
}

/** Paginated + API-filtered admissions list (used by the IPD list view). */
export function useAdmissionsPaginated(page = 1, limit = 20, filters?: AdmissionListFilters) {
  return useQuery({
    queryKey: ['admissions', 'paginated', page, limit, filters],
    queryFn: () => ipdService.getAdmissionsPaginated(page, limit, filters),
    staleTime: 15000,
    placeholderData: (previous) => previous,
  });
}

export function useAdmission(id?: string) {
  return useQuery({
    queryKey: ['admissions', 'detail', id],
    queryFn: () => ipdService.getAdmission(id!),
    enabled: !!id,
    staleTime: 15000,
  });
}

/**
 * A patient's active (not-yet-discharged) admission, if any. Used by the new
 * admission form to block a second active stay and show the existing one.
 */
export function useActiveAdmissionByPatient(patientId?: string) {
  return useQuery({
    queryKey: ['admissions', 'active-by-patient', patientId],
    queryFn: () => ipdService.getActiveAdmissionByPatient(patientId!),
    enabled: !!patientId,
    staleTime: 15000,
  });
}

export function useBulkDeleteAdmissions() {
  const queryClient = useQueryClient();
  return useMutation({
    meta: { hideToast: true },
    mutationFn: (ids: string[]) => ipdService.bulkDeleteAdmissions(ids),
    onSuccess: () => {
      invalidateAdmissionQueries(queryClient);
    },
  });
}

/** Soft-delete one admission; the page owns the confirmation and toast. */
export function useDeleteAdmission() {
  const queryClient = useQueryClient();
  return useMutation({
    meta: { hideToast: true },
    mutationFn: (id: string) => ipdService.deleteAdmission(id),
    onSuccess: () => {
      invalidateAdmissionQueries(queryClient);
    },
  });
}

/** Cancel one admission (keeps the record, releases the bed). */
export function useCancelAdmission() {
  const queryClient = useQueryClient();
  return useMutation({
    meta: { hideToast: true },
    mutationFn: (id: string) => ipdService.cancelAdmission(id),
    onSuccess: () => {
      invalidateAdmissionQueries(queryClient);
      // The backend also writes the stay's unpaid invoices off to the Risk
      // Fund (one ADMISSION entry), so refresh the source documents, patient
      // summaries and Risk Fund totals as well. `/ipd` only refreshes the
      // admission slices, which used to leave those screens showing the
      // pre-cancellation due.
      invalidateRiskFundImpact(queryClient);
    },
  });
}

export function useCreateBed() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ipdService.createBed,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['beds'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}

export function useUpdateBed() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => ipdService.updateBed(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['beds'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}

export function useDeleteBed() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => ipdService.deleteBed(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['beds'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}

export function useBulkDeleteBeds() {
  const queryClient = useQueryClient();
  return useMutation({
    meta: { hideToast: true },
    mutationFn: (ids: string[]) => ipdService.bulkDeleteBeds(ids),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['beds'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}

export function useCreateAdmission() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ipdService.createAdmission,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admissions'] });
      queryClient.invalidateQueries({ queryKey: ['beds'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}

/** Update admission metadata (clinical details + IPD referral commission). */
export function useUpdateAdmission() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateAdmissionDto }) =>
      ipdService.updateAdmission(id, data),
    onSuccess: () => {
      invalidateAdmissionQueries(queryClient);
      // A referral / flat-amount change can create, move or clear a payout.
      queryClient.invalidateQueries({ queryKey: ['payout-vouchers'] });
      queryClient.invalidateQueries({ queryKey: ['payee-outstanding'] });
      queryClient.invalidateQueries({ queryKey: ['payee-accruals'] });
      queryClient.invalidateQueries({ queryKey: ['revenue-distributions'] });
      queryClient.invalidateQueries({ queryKey: ['revenue-distributions-summary'] });
      queryClient.invalidateQueries({ queryKey: ['referrals'] });
    },
  });
}

export function useDischargeAdmission() {
  const queryClient = useQueryClient();
  return useMutation({
    // The IPD page handles the discharge gate itself (billing warning modal),
    // so suppress the generic success/error toast from the global mutation cache.
    meta: { hideToast: true },
    mutationFn: ({ id, data }: { id: string; data: any }) => ipdService.dischargeAdmission(id, data),
    onSuccess: () => {
      invalidateAdmissionQueries(queryClient);
    },
  });
}

export function useTransferBed() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, bedId }: { id: string; bedId: string }) => ipdService.transferBed(id, bedId),
    onSuccess: () => {
      invalidateAdmissionQueries(queryClient);
    },
  });
}

export function useStaffAssignments(admissionId?: string) {
  return useQuery({
    queryKey: ['admission-staff', admissionId],
    queryFn: () => ipdService.getStaffAssignments(admissionId!),
    enabled: !!admissionId,
    staleTime: 30000,
  });
}

export function useAssignStaff() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ admissionId, data }: { admissionId: string; data: Parameters<typeof ipdService.assignStaff>[1] }) =>
      ipdService.assignStaff(admissionId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['admission-staff', variables.admissionId] });
    },
  });
}

export function useRemoveStaffAssignment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ admissionId, assignmentId }: { admissionId: string; assignmentId: string }) =>
      ipdService.removeStaffAssignment(admissionId, assignmentId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['admission-staff', variables.admissionId] });
    },
  });
}

/** Charge events (and unbilled/billed totals) for an admission's visit. */
export function useAdmissionCharges(admissionId?: string) {
  return useQuery({
    queryKey: ['admission-charges', admissionId],
    queryFn: () => ipdService.getAdmissionCharges(admissionId!),
    enabled: !!admissionId,
    staleTime: 10000,
  });
}

/**
 * Billing clearance for an admission: unbilled charges plus every invoice's
 * due amount. Powers the quick-invoice dialog's "full pay all" flow.
 */
export function useAdmissionBillingStatus(
  admissionId?: string,
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: ['admission-billing-status', admissionId],
    queryFn: () => ipdService.getAdmissionBillingStatus(admissionId!),
    enabled: !!admissionId && (options?.enabled ?? true),
    staleTime: 10000,
  });
}

/** Add service-catalog items to an existing admission (charged to its visit). */
export function useAddAdmissionServices() {
  const queryClient = useQueryClient();
  return useMutation({
    meta: { successMessage: 'Service(s) added to the bill' },
    mutationFn: ({ admissionId, items }: { admissionId: string; items: AdmissionServiceInput[] }) =>
      ipdService.addAdmissionServices(admissionId, items),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['admission-charges', variables.admissionId] });
      queryClient.invalidateQueries({ queryKey: ['admissions'] });
      queryClient.invalidateQueries({ queryKey: ['unbilled-charges'] });
      queryClient.invalidateQueries({ queryKey: ['top-sheet'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['admission-billing-status'] });
    },
  });
}

/**
 * Changes the quantity of one unbilled charge. The backend re-prices the line
 * (minimum units / hourly rounding) and adjusts the patient ledger, so every
 * admission and billing summary is refreshed rather than patched locally.
 */
export function useUpdateAdmissionChargeQuantity() {
  const queryClient = useQueryClient();
  return useMutation({
    meta: { hideToast: true },
    mutationFn: ({
      admissionId,
      chargeId,
      quantity,
    }: {
      admissionId: string;
      chargeId: string;
      quantity: number;
    }) => ipdService.updateAdmissionChargeQuantity(admissionId, chargeId, quantity),
    onSuccess: () => {
      invalidateAdmissionQueries(queryClient);
    },
  });
}

/**
 * Removes an unbilled charge from an admission's visit. The page confirms the
 * action and shows the outcome, so the global success toast is suppressed.
 */
export function useDeleteAdmissionCharge() {
  const queryClient = useQueryClient();
  return useMutation({
    meta: { hideToast: true },
    mutationFn: ({
      admissionId,
      chargeId,
    }: {
      admissionId: string;
      chargeId: string;
    }) => ipdService.deleteAdmissionCharge(admissionId, chargeId),
    onSuccess: () => {
      invalidateAdmissionQueries(queryClient);
    },
  });
}

/** Bulk-remove unbilled charges; the caller reports the blocked/not-found ids. */
export function useBulkDeleteAdmissionCharges() {
  const queryClient = useQueryClient();
  return useMutation({
    meta: { hideToast: true },
    mutationFn: ({ admissionId, ids }: { admissionId: string; ids: string[] }) =>
      ipdService.bulkDeleteAdmissionCharges(admissionId, ids),
    onSettled: () => {
      invalidateAdmissionQueries(queryClient);
    },
  });
}

/**
 * Re-checks a single admission's bed charges, posting any nights that are still
 * outstanding (idempotent). Backs the Refresh action on the admission screen so
 * staff can recover a nightly charge that has not appeared yet.
 */
export function useRefreshAdmissionBedCharges() {
  const queryClient = useQueryClient();
  return useMutation({
    // The caller shows a precise toast based on the number of nights posted.
    meta: { hideToast: true },
    mutationFn: (admissionId: string) =>
      ipdService.refreshAdmissionBedCharges(admissionId),
    onSuccess: (_, admissionId) => {
      queryClient.invalidateQueries({ queryKey: ['admission-charges', admissionId] });
      queryClient.invalidateQueries({ queryKey: ['admissions'] });
      queryClient.invalidateQueries({ queryKey: ['unbilled-charges'] });
      queryClient.invalidateQueries({ queryKey: ['admission-billing-status', admissionId] });
      queryClient.invalidateQueries({ queryKey: ['top-sheet'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}
